import i18n from "@/i18n/config";
import { formatStock } from "@/lib/formatStock";

/**
 * Structured stock error returned by the inventory/completion edge functions.
 *
 * Backend codes we know about:
 *  - INSUFFICIENT_STOCK         -> global stock is too low
 *  - LOCATION_STOCK_INSUFFICIENT-> per-location stock is too low
 *  - LOCATION_STOCK_NOT_FOUND   -> product has no stock row at the resolved location
 *  - INVENTORY_DEDUCTION_FAILED -> generic deduction failure
 *  - PRODUCT_NOT_FOUND          -> product was deleted
 */
export interface StructuredStockError {
  error?: string;
  message?: string | null;
  product_name?: string | null;
  products?: string[] | null;
  required?: number | null;
  available?: number | null;
  unit?: string | null;
  location_name?: string | null;
  available_locations?: string[] | null;
}

/**
 * Translate a backend stock error into the user's selected language.
 * Always prefers structured fields over backend English `message`.
 */
export function translateStockError(err: StructuredStockError | null | undefined): string {
  const t = i18n.t.bind(i18n);
  if (!err) return t("inventory.inventoryDeductionFailed");

  const code = err.error;
  const productName = err.product_name || (err.products && err.products[0]) || "";
  const productList = (err.products && err.products.length > 0)
    ? err.products.join(", ")
    : productName;
  const locationName = err.location_name || "";
  const unit = err.unit || "";
  // String() de un float saca «20.329300000000007» en la cara del usuario, y estos
  // dos viajan a todos los avisos de stock insuficiente de la app.
  const required = err.required != null ? formatStock(err.required) : "";
  const available = err.available != null ? formatStock(err.available) : "";

  switch (code) {
    case "LOCATION_STOCK_NOT_FOUND": {
      if (productList && locationName) {
        /* «Confidor, Vertimec no está disponible en La Reina» no se lee: con más
           de un producto la frase va en plural y la lista al final. */
        const many = (err.products?.length ?? 0) > 1;
        return t(many ? "inventory.locationStockNotFoundMany" : "inventory.locationStockNotFound", {
          products: productList,
          location: locationName,
        });
      }
      if (productList) {
        return t("inventory.locationStockNotFoundGeneric", { products: productList });
      }
      return t("inventory.locationStockNotFoundGeneric", { products: "" });
    }
    case "LOCATION_STOCK_INSUFFICIENT": {
      if (productName && locationName && required && available) {
        return t("inventory.locationStockInsufficient", {
          product: productName,
          location: locationName,
          required,
          available,
          unit,
        });
      }
      return t("inventory.locationStockInsufficientGeneric");
    }
    case "INSUFFICIENT_STOCK": {
      if (productName && required && available) {
        return t("inventory.insufficientStockDetail", {
          productName,
          required,
          available,
        });
      }
      return t("inventory.insufficientStock");
    }
    case "INVENTORY_DEDUCTION_FAILED":
      /* Cuando el servidor SABE de qué producto habla, se dice.
         «No se pudo descontar el inventario. Inténtalo de nuevo.» es lo que leyó
         el usuario el 26-08-2026 al intentar completar un tratamiento cuya dosis
         iba en litros y el producto estaba en kilos: no dice qué producto, no
         dice por qué, y «inténtalo de nuevo» es un mal consejo porque volver a
         intentarlo daba exactamente lo mismo. El servidor mandaba el nombre en
         `product_name` y esta función lo tiraba a la basura. */
      if (productName) {
        return t("inventory.inventoryDeductionFailedProduct", { product: productName });
      }
      return t("inventory.inventoryDeductionFailed");
    case "PRODUCT_NOT_FOUND": {
      if (productName) {
        return t("planner.productNotFoundDetail", { productName });
      }
      return t("planner.productNotFound");
    }
    default:
      return err.message || t("inventory.inventoryDeductionFailed");
  }
}

/**
 * Returns true if the code is one of the stock-related blocking errors
 * that should open the stockout dialog.
 */
export function isStockErrorCode(code?: string | null): boolean {
  return (
    code === "INSUFFICIENT_STOCK" ||
    code === "LOCATION_STOCK_NOT_FOUND" ||
    code === "LOCATION_STOCK_INSUFFICIENT" ||
    code === "INVENTORY_DEDUCTION_FAILED"
  );
}
